"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { useId } from "react";
import { RegisterOptions, useFormContext, useFormState } from "react-hook-form";
import styles from "./RadioGroupField.module.scss";
import { RadioInput } from "../../input/RadioInput/RadioInput";
import clsx from "clsx";

type Option = {
	label: React.ReactNode;
	value: string;
};

export interface RadioGroupFieldProps {
	name: string;
	label: string;
	hideLabel?: boolean;
	options?: Option[];
	className?: string;
	innerClassName?: string;
	optionClassName?: string;
	labelClassName?: string;
	inputClassName?: string;
	help?: string;
	registerOptions?: RegisterOptions;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
}

export const RadioGroupField = ({
	name,
	label,
	hideLabel,
	options,
	className,
	innerClassName,
	optionClassName,
	labelClassName,
	inputClassName,
	help,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
}: RadioGroupFieldProps) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { register } = useFormContext();
	const { errors } = useFormState();

	return (
		<FieldWrapper
			{...{
				name,
				label,
				hideLabel,
				required: registerOptions?.required as boolean,
				labelId: id,
				className,
				errorId,
			}}
		>
			<div className={clsx(styles["radio-group-wrapper"])}>
				<div
					className={clsx(
						styles["radio-group"],
						innerClassName,
						hideLabel && styles["radio-group--hidden-label"],
					)}
					role="radiogroup"
					aria-labelledby={id}
				>
					{options?.map(radio => (
						<RadioInput
							value={radio.value}
							key={radio.value}
							label={radio.label}
							className={optionClassName}
							labelClassName={labelClassName}
							inputClassName={inputClassName}
							aria-describedby={
								[help ? helpId : "", errors[name] ? errorId : "", ariaDescribedBy]
									.filter(v => v)
									.join(" ") || undefined
							}
							{...register(name, registerOptions)}
							{...inputProps}
						/>
					))}
				</div>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
